/**
 * journey-ux/DataGrid.jsx — dense spreadsheet-style table (Phase 1)
 *
 * Implements the universal grid mechanics from COORDINATOR_GRID_DESIGN.md:
 *   - 32 px row heights (12 px text, 6 px vertical cell padding)
 *   - Sticky column header
 *   - Inline pills (via the <Pill> primitive)
 *   - Built-in checkbox bulk selection (first column)
 *   - Row click → onRowClick (the parent decides what to show, typically
 *     opens <DetailDrawer>)
 *   - Sortable / configurable via column definitions
 *
 * Props:
 *   columns: [{
 *     id, header, accessor?(row, i) | render?(row, i),
 *     width? (tailwind class e.g. 'w-24'),
 *     align? ('left' | 'right' | 'center')
 *   }]
 *   rows: any[]
 *   rowKey: row => string
 *   selectedIds: Set<string>
 *   onToggleRow: (id) => void
 *   onToggleAll: () => void
 *   onRowClick: (row) => void
 *   activeRowId?: string   (highlights the row currently shown in drawer)
 *   maxHeightClass?: string (default 'h-[calc(100vh-260px)]')
 *   emptyMessage?: ReactNode
 *   loadingMessage?: ReactNode
 *   loading?: boolean
 *   testId?: string
 */
import React from 'react';

const ALIGN = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

export const DataGrid = ({
  columns,
  rows,
  rowKey,
  selectedIds = new Set(),
  onToggleRow,
  onToggleAll,
  onRowClick,
  activeRowId = null,
  maxHeightClass = 'h-[calc(100vh-260px)]',
  emptyMessage = 'No rows.',
  loadingMessage = 'Loading…',
  loading = false,
  testId = 'data-grid',
  selectable = true,
}) => {
  const allSelected =
    selectable && rows.length > 0 && rows.every((r) => selectedIds.has(rowKey(r)));

  return (
    <div
      data-testid={testId}
      className={`overflow-auto ${maxHeightClass} bg-white border-t border-slate-100`}
    >
      <table className="w-full border-collapse text-[12px]" data-testid={`${testId}-table`}>
        <thead className="sticky top-0 z-10">
          <tr>
            {selectable && (
              <th
                className="px-3 py-2 w-7 text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200 border-r border-slate-100"
                data-testid={`${testId}-select-all-header`}
              >
                <input
                  type="checkbox"
                  className="rounded border-slate-300"
                  checked={allSelected}
                  onChange={onToggleAll}
                  data-testid={`${testId}-select-all`}
                  aria-label="Select all rows"
                />
              </th>
            )}
            {columns.map((c) => (
              <th
                key={c.id}
                className={`px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200 border-r border-slate-100 ${ALIGN[c.align || 'left']} ${c.width || ''}`}
                data-testid={`${testId}-head-${c.id}`}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td
                colSpan={(selectable ? 1 : 0) + columns.length}
                className="px-6 py-10 text-center text-slate-500 text-[12px]"
                data-testid={`${testId}-loading`}
              >
                {loadingMessage}
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td
                colSpan={(selectable ? 1 : 0) + columns.length}
                className="px-6 py-10 text-center text-slate-500 text-[12px]"
                data-testid={`${testId}-empty`}
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row, i) => {
              const rawId = rowKey(row);
              const id = (rawId == null || rawId === '') ? `row-${i}` : rawId;
              const isActive = activeRowId === id;
              const isSelected = selectedIds.has(id);
              return (
                <tr
                  key={`${id}-${i}`}
                  data-testid={`${testId}-row-${id}`}
                  className={`border-b border-slate-100 transition-colors cursor-pointer ${
                    isActive ? 'bg-sky-50' : isSelected ? 'bg-slate-50' : 'hover:bg-slate-50/70'
                  }`}
                  onClick={(e) => {
                    // Don't trigger row click if user clicked on a checkbox or button.
                    if (
                      e.target.tagName === 'INPUT' ||
                      e.target.closest('button') ||
                      e.target.closest('a')
                    ) {
                      return;
                    }
                    onRowClick && onRowClick(row);
                  }}
                >
                  {selectable && (
                    <td
                      className="px-3 py-1.5 w-7 text-center border-r border-slate-100"
                      data-testid={`${testId}-row-${id}-select`}
                    >
                      <input
                        type="checkbox"
                        className="rounded border-slate-300"
                        checked={isSelected}
                        onChange={() => onToggleRow(id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label="Select row"
                      />
                    </td>
                  )}
                  {columns.map((c) => {
                    const content = c.render
                      ? c.render(row, i)
                      : c.accessor
                        ? c.accessor(row, i)
                        : '—';
                    return (
                      <td
                        key={c.id}
                        data-testid={`${testId}-row-${id}-cell-${c.id}`}
                        className={`px-3 py-1.5 text-[12px] text-slate-700 border-r border-slate-100 ${ALIGN[c.align || 'left']} ${c.cellClassName || ''}`}
                      >
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DataGrid;
